import {
  faTimes, faSave, faArrowCircleLeft,
  faArrowCircleRight, faUndo, faSync
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Context as InteractionContext } from "./Interaction/InteractionContext";
import { Context as InstanceDataContext } from "./Positionable/InstanceDataContext";
import { Context as DataSourceContext } from "./DataSourceContext"
import { ProjectContext } from '../../contexts/ProjectContext';
import { useHistory } from 'react-router-dom';
import { MapEditorRouteContext } from "../CustomRoutes/MapEditor";
import ReactTooltip from 'react-tooltip';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import AppContext from '../../contexts/AppContext';

/**
 * Initial state of options
 *
 * @typedef defaultOptions
 * @type {Array}
 */

function CancelButton(props) {
  return (
    <>
      <button className='pure-button pure-button-secondary' {...props}>
        <span>Cancel</span>
        <FontAwesomeIcon icon={faTimes} {...props} />
      </button>
    </>
  );
}

function UndoButton(props) {
  return (
    <>
      <button className='pure-button pure-button-secondary' {...props}>
        <span>Undo</span>
        <FontAwesomeIcon icon={faUndo} />
      </button>
    </>
  );
}

/**
 * Map Editor "Toolbar"
 *
 * Only "shows" the active tools, controlled by user interactions on map
 * Provides api for user to save or discard changes
 *
 * @todo separator in options
 */
export function Toolbar(props) {
  const { mapId, divisionId, projectId } = props;
  const interactionContext = useContext(InteractionContext);
  const dataContext = useContext(InstanceDataContext);
  const dc = useContext(DataSourceContext)
  const [busy, setBusy] = useState(false);
  const isTabletOrLess = useMediaQuery({ maxWidth: 768 });
  const [disableLeft, setDisableLeft] = useState(false);
  const [disableRight, setDisableRight] = useState(false);
  const [getMapId, setGetMapId] = useState(mapId);
  const [redirect, setRedirect] = useState();
  const projectContext = useContext(ProjectContext);
  const mapEditorRouteContext = useContext(MapEditorRouteContext);
  const { mapMobileView, setMapMobileView } = mapEditorRouteContext;
  const appContext = useContext(AppContext);
  const userRole = appContext.get('user')?.roleName;
  const canViewCalibrateMap = userRole === 'Super Admin' || userRole === 'Admin';
  const [filterDocs, setFilterDocs] = useState([])
  const [isArchive, setIsArchive] = useState(false)
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showCalibrateMap, setShowCalibrateMap] = useState(mapEditorRouteContext.showCalibrateMap || false);
  
  if (!dataContext) {
    return null;
  }

  // Check if user is on the most recent inspection
  const isRecentInsp = 
    mapEditorRouteContext.inspections?.length > 0 && 
    mapEditorRouteContext.inspections[0].id === mapEditorRouteContext.selectedInspection?.id;
  
  const selectedInspectionId = mapEditorRouteContext.selectedInspection?.id;
  const isInitialInspection = mapEditorRouteContext.inspections?.length === 1;
  const allowEditDueToSpecialInspection = !(selectedInspectionId) || isInitialInspection;

  const onDiscard = () => {
    dataContext.discard();
    interactionContext.setPositionableType(null);
  };

  const onUndo = () => {
    dataContext.undo();
  };
  
  const handleSaveClick = () => {
    // If user is on recent inspection, save directly without confirmation
    if (isRecentInsp || allowEditDueToSpecialInspection) {
      onSave();
    } else {
      // Otherwise, show confirmation modal
      setShowSaveConfirmation(true);
    }
  };
  
  const onSave = () => {
    setShowSaveConfirmation(false);
    setBusy(true);
    interactionContext.setDefaultMode();

    dataContext.save().then(
      () => {
        interactionContext.setPositionableSourceModel(null);
        setBusy(false);
      },
      (err) => {
        setBusy(false);

        return Promise.reject(err);
      },
    );
  };

  useEffect(() => {
    let filteredArr = []
    projectContext.projectDocs && projectContext.projectDocs.documentTypes
      .filter((ele) => ele.name === 'Active Site Maps')[0]
      .documents.filter((e) => {
        const getArchiveText = window.location.href.split('/')
        const archiveIndex = getArchiveText.length-1
        
        if(getArchiveText[archiveIndex].endsWith("archive")){
          if(e.archived_at){
            filteredArr.push(e.GUID)
            setFilterDocs(filteredArr)
            setIsArchive(true)
          }
        }
       else{
         if(!e.archived_at){
           filteredArr.push(e.GUID)
           setFilterDocs(filteredArr)
          }
       }

        return e
      });

      let counter = filteredArr.indexOf(mapId);

    if (counter === 0) {
      setDisableLeft(true);
    }else{
      setDisableLeft(false);
    }

    if (counter === filteredArr.length - 1) {
      setDisableRight(true);
    }else{
      setDisableRight(false);
    }


  }, [projectContext.projectDocs]);

  // Sync showCalibrateMap state with context
  useEffect(() => {
    if (mapEditorRouteContext.showCalibrateMap !== undefined) {
      setShowCalibrateMap(mapEditorRouteContext.showCalibrateMap);
    }
  }, [mapEditorRouteContext.showCalibrateMap]);
  
  const toggleMap = (name) => {
    let counter = filterDocs.indexOf(mapId);

    if (name === 'toggleLeft') {
      if (counter === 0) {
        setRedirect(false);
      } else {
        setRedirect(true);
        setGetMapId(filterDocs[counter - 1]);
      }
    }
    if (name === 'toggleRight') {
      if (counter === filterDocs.length - 1) {
        setRedirect(false);
      } else {
        setRedirect(true);
        setGetMapId(filterDocs[counter + 1]);
      }
    }
  };

  const getUrl = useHistory();
  const getStr = getUrl.location.search;
  const testReg = getStr.match('active')

  if (redirect === true) {
    const ext = testReg !== null ? 'active-map' : 'inspection'
    const divId = divisionId || localStorage.getItem("divisionId")
    const isPublic = window.location.pathname.startsWith("/public")

    window.location.href = isPublic ? `/public/divisions/${divId}/projects/${projectId}/map/${getMapId}/edit?from=${ext}${isArchive ? '&archive' : ''}` : `/divisions/${divId}/projects/${projectId}/map/${getMapId}/edit?from=${ext}${isArchive ? '&archive' : ''}`
  }

  return (
    <div
      className={
        'map-editor-toolbar'
      }
      style={{ justifyContent: 'space-between' }}
    >
      <div
        className={'map-editor-Angle'}
        onClick={() => toggleMap('toggleLeft')}
      >
        {disableLeft ? null : <FontAwesomeIcon icon={faArrowCircleLeft} />}
      </div>

      {dataContext.hasPendingUpdates && (
        <div className='map-editor-toolbar-actions'>
          <CancelButton onClick={onDiscard} />

          <button
            className='pure-button pure-button-primary'
            onClick={handleSaveClick}
            disabled={busy}
          >
            <span>Save</span>
            <FontAwesomeIcon
              className='map-editor-save-icon'
              icon={faSave}
            />
          </button>
          {dc.undo.length ? (
            <UndoButton onClick={onUndo} />
          ) : null}
        </div>
      )}

      {!dataContext.hasPendingUpdates && dc.undo.length ? (
        <>
          <div className='map-editor-toolbar-actions'>
            <UndoButton onClick={onUndo} />
          </div>
        </>
      ) : null}

      {!dataContext.hasPendingUpdates && interactionContext.mode === 'modify' && (
        <div className='map-editor-toolbar-actions'>
          <CancelButton onClick={onDiscard} isTabletOrLess={isTabletOrLess} />
        </div>
      )}

      <div className="map-editor-toolbar__right-most">
        { isTabletOrLess ? 
          <></> :
          <>
            {!mapMobileView && canViewCalibrateMap && (
              <span 
                onClick={() => {
                  const newShowCalibrateMap = !showCalibrateMap;
                  setShowCalibrateMap(newShowCalibrateMap);
                  if (mapEditorRouteContext.setShowCalibrateMap) {
                    mapEditorRouteContext.setShowCalibrateMap(
                      newShowCalibrateMap,
                    );
                  }
                }} 
                className="map-editor-toolbar__view-button"
                style={{
                  backgroundColor: showCalibrateMap || mapEditorRouteContext.showCalibrateMap ? '#002230' : 'inherit',
                }}
              >
                Calibrate Map
              </span>
            )}
<span onClick={() => setMapMobileView(!mapMobileView)} className="map-editor-toolbar__view-button">{mapMobileView ? "Desktop View" : "Mobile View" }</span>
          </>
        }

        <div
          className={'map-editor-Angle'}
          onClick={() => toggleMap('toggleRight')}
        >
          {disableRight ? null : <FontAwesomeIcon icon={faArrowCircleRight} />}
        </div>
      </div>

      <ConfirmationModal
        show={showSaveConfirmation}
        handleConfirm={onSave}
        handleClose={() => setShowSaveConfirmation(false)}
        title='Are you sure you want to save these changes?'
        subTitle={`You're making changes to an older version of this site map. Once saved, any legend items you add cannot be edited, unless done in the most recent version of the map. Do you wish to continue?`}
        buttonText='Yes, Save Changes'
      />
    </div>
  );
}

export default Toolbar;